import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

export interface MemberReportFilters {
  searchTerm?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface MemberReportExportParams<TSortKey extends string>
  extends MemberReportFilters {
  sortBy?: TSortKey;
  sortOrder?: "ASC" | "DESC";
}

export interface MemberReportPageParams<TSortKey extends string>
  extends MemberReportExportParams<TSortKey> {
  page: number;
  pageSize: number;
}

export interface MemberReportPageResponse<TItem> {
  data: TItem[];
  meta?: {
    totalCount: number;
  };
}

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

interface UseMemberReportOptions<TItem, TSortKey extends string> {
  /** Stable reference (module level); it is a dependency of the fetch effect. */
  fetchPage: (
    params: MemberReportPageParams<TSortKey>,
    signal: AbortSignal,
  ) => Promise<MemberReportPageResponse<TItem>>;
  /** The backend's sort whitelist. Any other column id is sent unsorted. */
  sortKeys: readonly TSortKey[];
  initialFilters: MemberReportFilters;
}

function resolveSortBy<TSortKey extends string>(
  sorting: SortingState,
  sortKeys: readonly TSortKey[],
): TSortKey | undefined {
  if (sorting.length === 0) return undefined;
  const sortId = sorting[0].id;
  return sortKeys.find((key) => key === sortId);
}

function buildExportParams<TSortKey extends string>(
  filters: MemberReportFilters,
  sorting: SortingState,
  sortKeys: readonly TSortKey[],
): MemberReportExportParams<TSortKey> {
  const params: MemberReportExportParams<TSortKey> = {};
  if (filters.searchTerm) params.searchTerm = filters.searchTerm;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  const sortBy = resolveSortBy(sorting, sortKeys);
  if (sortBy) {
    params.sortBy = sortBy;
    params.sortOrder = sorting[0].desc ? "DESC" : "ASC";
  }
  return params;
}

/**
 * Paged, sorted, filtered member report state shared by the member-facing
 * event reports. Local state only, as My Training History does.
 *
 * Every query change aborts the request in flight, so a slow earlier page can
 * never land on top of a faster later one (docs/architecture/request-cancellation.md).
 */
export function useMemberReport<TItem, TSortKey extends string>({
  fetchPage,
  sortKeys,
  initialFilters,
}: UseMemberReportOptions<TItem, TSortKey>) {
  const [items, setItems] = useState<TItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<MemberReportFilters>(initialFilters);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const createSignal = useCallback(() => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  }, [cancelPendingRequest]);

  const exportParams = useMemo(
    () => buildExportParams(filters, sorting, sortKeys),
    [filters, sorting, sortKeys],
  );

  useEffect(() => {
    const signal = createSignal();
    setLoading(true);

    fetchPage(
      {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...exportParams,
      },
      signal,
    )
      .then((response) => {
        setItems(response.data);
        setTotalCount(response.meta?.totalCount ?? 0);
        setLoading(false);
      })
      .catch((error) => {
        // A superseded request settles after its replacement has raised the
        // flag, so it must not lower it. Real failures were already toasted
        // by the interceptor; there is nothing left to do but stop the spinner.
        if (!axios.isCancel(error)) setLoading(false);
      });
  }, [fetchPage, pagination, exportParams, createSignal]);

  useEffect(() => cancelPendingRequest, [cancelPendingRequest]);

  const handlePaginationChange = useCallback((newPagination: Pagination) => {
    setPagination(newPagination);
  }, []);

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) =>
        prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
      );
    },
    [],
  );

  const handleFilterChange = useCallback(
    (key: keyof MemberReportFilters, value: string | undefined) => {
      setFilters((prev) =>
        prev[key] === value ? prev : { ...prev, [key]: value },
      );
      setPagination((prev) =>
        prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
      );
    },
    [],
  );

  return {
    items,
    totalCount,
    loading,
    pagination,
    sorting,
    filters,
    exportParams,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
